import { useEffect, useRef } from 'react';
import { captureEvent } from '../utils/posthog';
import { EVENTS, PROPERTIES, SCROLL_MILESTONES } from '../utils/analytics';

/**
 * Hook to track scroll depth milestones
 * Tracks when user reaches 25%, 50%, 75%, and 100% scroll depth
 */
export const useScrollTracking = () => {
  const trackedMilestonesRef = useRef(new Set());
  const pageLoadTimeRef = useRef(Date.now());

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Calculate current scroll depth
      const scrollDepth = Math.round(
        ((scrollTop + windowHeight) / documentHeight) * 100
      );

      // Check each milestone
      SCROLL_MILESTONES.forEach((milestone) => {
        if (
          scrollDepth >= milestone &&
          !trackedMilestonesRef.current.has(milestone)
        ) {
          trackedMilestonesRef.current.add(milestone);

          const timeToMilestone = Math.round(
            (Date.now() - pageLoadTimeRef.current) / 1000
          );

          captureEvent(EVENTS.SCROLL_DEPTH_MILESTONE, {
            [PROPERTIES.SCROLL_DEPTH]: milestone,
            time_to_milestone: timeToMilestone,
            scroll_position: scrollTop,
            page_height: documentHeight,
            viewport_height: windowHeight,
          });
        }
      });
    };

    // Throttle scroll events for performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });

    // Track initial page load
    pageLoadTimeRef.current = Date.now();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, []);

  // Track time on page when component unmounts or user leaves
  useEffect(() => {
    const handleBeforeUnload = () => {
      const timeOnPage = Math.round(
        (Date.now() - pageLoadTimeRef.current) / 1000
      );

      captureEvent(EVENTS.TIME_ON_PAGE, {
        time_on_page: timeOnPage,
        scroll_depth_reached: Math.max(...Array.from(trackedMilestonesRef.current)),
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
};

